﻿<template>
  <dropdown
    ref="dropdownRef"
    :title="field.name"
    :class="{ 'has-calendar': showCalendar }"
    class="ifp-filter-custom-field"
    placement="bottom"
    @close="handleDropdownClose()"
  >
    <base-ifp-filter
      :label="field.name"
      removable
      @close-clicked="$emit('removed')"
    >
      <template #value-display>
        {{ valueDisplay }}
      </template>
    </base-ifp-filter>
    <template #content>
      <div>
        <!-- DROPDOWN: any of the chosen options -->
        <ul v-if="isDropdown">
          <li
            v-for="option in field.options"
            :key="option.id"
            @click="toggleOption(option.id)"
          >
            <span :class="{ 'selected': isOptionSelected(option.id) }">
              <span :class="`ifp-filter-custom-field__swatch ${option.color}`" />
              <span>{{ option.name }}</span>
            </span>
          </li>
          <!-- Items that have no option chosen for this field -->
          <li @click="toggleOption(NO_OPTION_FILTER_ID)">
            <span :class="{ 'selected': isOptionSelected(NO_OPTION_FILTER_ID) }">
              <span class="ifp-filter-custom-field__swatch" />
              <span>{{ NO_OPTION_FILTER_LABEL }}</span>
            </span>
          </li>
        </ul>

        <!-- CHECKBOX -->
        <ul v-else-if="isCheckbox">
          <li @click="setChecked(true)">
            <span :class="{ 'selected': isChecked === true }">
              <i class="ddi ddi-check" /><span>Checked</span>
            </span>
          </li>
          <li @click="setChecked(false)">
            <span :class="{ 'selected': isChecked === false }">
              <i class="ddi ddi-close" /><span>Unchecked</span>
            </span>
          </li>
        </ul>

        <!-- DATE: mirrors the due date filter -->
        <ul v-else-if="isDate">
          <li @click="setDateMode(ECustomFieldDateFilterModes.Any)">
            <span :class="{ 'selected': dateMode === ECustomFieldDateFilterModes.Any }">Has a date</span>
          </li>
          <li @click="setDateMode(ECustomFieldDateFilterModes.None)">
            <span :class="{ 'selected': dateMode === ECustomFieldDateFilterModes.None }">No date</span>
          </li>
          <li
            v-if="$screen.isTabletOrDesktop($mq)"
            :class="{ 'has-calendar': showCalendar }"
            @click="readyToSelectRange = true"
          >
            <span :class="{ 'selected': dateMode === ECustomFieldDateFilterModes.Range }">In date range...</span>
            <div>
              <due-date-range-picker
                v-show="showCalendar"
                v-model="calendarValue"
                :format="dateFormat"
                unlink-panels
                @selected-range="handleSelectedRange"
              />
            </div>
          </li>
        </ul>

        <!-- NUMBER -->
        <ul v-else-if="isNumber">
          <li @click="setNumberMode(ECustomFieldNumberFilterModes.Any)">
            <span :class="{ 'selected': numberMode === ECustomFieldNumberFilterModes.Any }">Has a value</span>
          </li>
          <li @click="setNumberMode(ECustomFieldNumberFilterModes.None)">
            <span :class="{ 'selected': numberMode === ECustomFieldNumberFilterModes.None }">No value</span>
          </li>
          <li class="ifp-filter-custom-field__range">
            <span :class="{ 'selected': numberMode === ECustomFieldNumberFilterModes.Range }">In range...</span>
            <div class="ifp-filter-custom-field__range-inputs">
              <el-input
                v-model="minDraft"
                :step="numberStep"
                type="number"
                size="small"
                placeholder="Min"
                @keyup.enter="applyNumberRange()"
              />
              <span class="ifp-filter-custom-field__range-separator">to</span>
              <el-input
                v-model="maxDraft"
                :step="numberStep"
                type="number"
                size="small"
                placeholder="Max"
                @keyup.enter="applyNumberRange()"
              />
              <dd-button
                :is-disabled="!hasNumberRangeDraft"
                size="small"
                type="flat"
                @click="applyNumberRange()"
              >Apply</dd-button>
            </div>
            <p v-if="numberRangeError" class="ifp-filter-custom-field__range-error">{{ numberRangeError }}</p>
          </li>
        </ul>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import dayjs from '@/helpers/dayjs'
import { debounce } from 'lodash-es'

/* Helpers */
import CustomFieldAssistant, {
  ECustomFieldDateFilterModes,
  ECustomFieldNumberFilterModes,
  ECustomFieldTypes,
  ICustomFieldFilter,
  NO_OPTION_FILTER_ID,
  NO_OPTION_FILTER_LABEL,
} from '@/helpers/custom-field-assistant'

/* Data types */
import { ICustomField } from '@/api/types/response.types'

export default defineComponent({
  name: 'IfpFilterCustomField',

  props: {
    field: {
      type: Object as () => ICustomField,
      required: true,
    },
    /* The active filter on this field, or null when the chip is exposed but nothing is chosen yet */
    filter: {
      type: Object as () => ICustomFieldFilter,
      default: null,
    },
  },

  emits: ['filter-updated', 'removed'],

  data() {
    return {
      ECustomFieldDateFilterModes,
      ECustomFieldNumberFilterModes,
      NO_OPTION_FILTER_ID,
      NO_OPTION_FILTER_LABEL,
      /* Dropdown selections update the UI immediately and emit after a short pause so multiple clicks make one search */
      internalOptionIDs: [] as number[],
      debounceEmitOptionIDs: null as any,
      emitDebounceTimeForMultipleSelectionsInMS: 500,
      /* Date range */
      readyToSelectRange: false,
      calendarValue: [] as any[],
      dateFormat: 'MMMM DD, YYYY',
      /* Number range, typed but not yet applied */
      minDraft: '',
      maxDraft: '',
      numberRangeError: '',
    }
  },

  computed: {
    isDropdown(): boolean {
      return this.field.customFieldType === ECustomFieldTypes.Dropdown
    },
    isCheckbox(): boolean {
      return this.field.customFieldType === ECustomFieldTypes.Checkbox
    },
    isDate(): boolean {
      return this.field.customFieldType === ECustomFieldTypes.Date
    },
    isNumber(): boolean {
      return this.field.customFieldType === ECustomFieldTypes.Number
    },
    numberMode(): ECustomFieldNumberFilterModes | null {
      return this.filter ? this.filter.numberMode || null : null
    },
    numberStep(): string {
      return CustomFieldAssistant.numberStepFor(this.field)
    },
    hasNumberRangeDraft(): boolean {
      return (
        String(this.minDraft).trim().length > 0 ||
        String(this.maxDraft).trim().length > 0
      )
    },
    isChecked(): boolean | null {
      return this.filter && this.filter.checked !== undefined
        ? this.filter.checked
        : null
    },
    dateMode(): ECustomFieldDateFilterModes | null {
      return this.filter ? this.filter.dateMode || null : null
    },
    showCalendar(): boolean {
      return (
        this.isDate &&
        (this.dateMode === ECustomFieldDateFilterModes.Range ||
          this.readyToSelectRange)
      )
    },
    valueDisplay(): string {
      if (this.isDropdown) {
        // Reflect clicks immediately rather than waiting for the debounced emit
        return CustomFieldAssistant.describeOptionIDs(
          this.field,
          this.internalOptionIDs,
        )
      }
      return CustomFieldAssistant.describeFilter(this.field, this.filter)
    },
  },

  watch: {
    filter: {
      immediate: true,
      handler(filter: ICustomFieldFilter) {
        this.internalOptionIDs =
          filter && filter.optionIDs ? filter.optionIDs : []
        const start =
          filter && filter.startDate ? dayjs(filter.startDate) : dayjs()
        const end = filter && filter.endDate ? dayjs(filter.endDate) : dayjs()
        this.calendarValue = [start, end]
        this.minDraft =
          filter && filter.minValue !== null && filter.minValue !== undefined
            ? String(filter.minValue)
            : ''
        this.maxDraft =
          filter && filter.maxValue !== null && filter.maxValue !== undefined
            ? String(filter.maxValue)
            : ''
        this.numberRangeError = ''
      },
    },
  },

  created() {
    this.debounceEmitOptionIDs = debounce(
      this._emitOptionIDs,
      this.emitDebounceTimeForMultipleSelectionsInMS,
    )
  },

  methods: {
    emitFilter(filter: ICustomFieldFilter | null) {
      this.$emit('filter-updated', filter)
    },

    closeDropdown() {
      if (this.$refs.dropdownRef) {
        // @ts-ignore
        this.$refs.dropdownRef.handleClose()
      }
    },

    handleDropdownClose() {
      // Chose "In date range..." but never picked one
      this.readyToSelectRange = false
      this.numberRangeError = ''
    },

    /* ---------- Number ---------- */

    setNumberMode(mode: ECustomFieldNumberFilterModes) {
      this.emitFilter(
        this.numberMode === mode
          ? null
          : { fieldID: this.field.id, numberMode: mode },
      )
      this.closeDropdown()
    },

    applyNumberRange() {
      const minRaw = String(this.minDraft).trim()
      const maxRaw = String(this.maxDraft).trim()
      const minValue = minRaw.length > 0 ? Number(minRaw) : null
      const maxValue = maxRaw.length > 0 ? Number(maxRaw) : null

      if (minValue === null && maxValue === null) {
        return
      }
      if (
        (minValue !== null && !isFinite(minValue)) ||
        (maxValue !== null && !isFinite(maxValue))
      ) {
        this.numberRangeError = 'Enter valid numbers.'
        return
      }
      if (minValue !== null && maxValue !== null && minValue > maxValue) {
        this.numberRangeError = 'Min cannot be more than max.'
        return
      }

      this.numberRangeError = ''
      this.emitFilter({
        fieldID: this.field.id,
        numberMode: ECustomFieldNumberFilterModes.Range,
        minValue,
        maxValue,
      })
      this.closeDropdown()
    },

    /* ---------- Dropdown ---------- */

    isOptionSelected(optionID: number): boolean {
      return this.internalOptionIDs.includes(optionID)
    },

    toggleOption(optionID: number) {
      if (this.internalOptionIDs.includes(optionID)) {
        this.internalOptionIDs = this.internalOptionIDs.filter(
          id => id !== optionID,
        )
      } else {
        this.internalOptionIDs = [...this.internalOptionIDs, optionID]
      }
      this.debounceEmitOptionIDs()
    },

    _emitOptionIDs() {
      this.emitFilter(
        this.internalOptionIDs.length > 0
          ? { fieldID: this.field.id, optionIDs: [...this.internalOptionIDs] }
          : null,
      )
    },

    /* ---------- Checkbox ---------- */

    setChecked(checked: boolean) {
      // Clicking the chosen state again clears the filter
      this.emitFilter(
        this.isChecked === checked ? null : { fieldID: this.field.id, checked },
      )
      this.closeDropdown()
    },

    /* ---------- Date ---------- */

    setDateMode(mode: ECustomFieldDateFilterModes) {
      this.readyToSelectRange = false
      this.emitFilter(
        this.dateMode === mode
          ? null
          : { fieldID: this.field.id, dateMode: mode },
      )
      this.closeDropdown()
    },

    handleSelectedRange(range: any[]) {
      if (!this.showCalendar || !range || range.length < 2) {
        return
      }
      this.readyToSelectRange = false
      this.emitFilter({
        fieldID: this.field.id,
        dateMode: ECustomFieldDateFilterModes.Range,
        startDate: dayjs(range[0]).format('YYYY-MM-DD'),
        endDate: dayjs(range[1]).format('YYYY-MM-DD'),
      })
      this.closeDropdown()
    },
  },
})
</script>

<style lang="scss">
.ifp-filter-custom-field {
  /* Dropdown option color, shown as a dot beside the option name */
  &__swatch {
    display: inline-block;
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    margin-right: 6px;
    border-radius: 50%;
    background-color: var(--color-text-third);

    &.dodger-blue {
      background-color: $dodger-blue;
    }
    &.viridian {
      background-color: $viridian;
    }
    &.algae-green {
      background-color: $algae-green;
    }
    &.bluish-grey {
      background-color: $bluish-grey;
    }
    &.bluey-purple {
      background-color: $bluey-purple;
    }
    &.strawberry {
      background-color: $strawberry;
    }
    &.darkish-pink {
      background-color: $darkish-pink;
    }
    &.reddish-orange {
      background-color: $reddish-orange;
    }
    &.light-orange {
      background-color: $light-orange;
    }
  }

  /* The dropdown skips its icon spacing for .ddi-check (it doubles as the "selected" mark), so space the checkbox icons here */
  ul li > span > .ddi-check,
  ul li > span > .ddi-close {
    margin-right: 8px;
    color: var(--color-text-third);
  }

  /* Number range entry sits inside its own list item */
  &__range {
    flex-direction: column;
    align-items: flex-start !important;
    cursor: default !important;

    &:hover {
      background: transparent !important;
    }

    > span {
      cursor: default;
    }
  }

  &__range-inputs {
    display: flex;
    align-items: center;
    margin-top: 6px;
    @include flex-row-horizontal-spacing(6px);

    .el-input {
      width: 90px;
    }
  }

  &__range-separator {
    @include lettering--p-sm;
    color: var(--color-text-third);
  }

  &__range-error {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: $strawberry;
  }

  /* Date range layout borrowed from the due date filter */
  ul {
    li {
      .due-date-range-picker__inputs {
        display: none;
      }

      &.has-calendar {
        max-width: 144px;
      }

      .due-date-range-picker {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 9999;
        float: right;
        width: 538px;
        height: 248px;
        padding: 0;
        overflow: hidden;

        .el-date-range-picker {
          padding: 0 !important;
        }

        .el-picker-panel {
          top: 0 !important;
        }

        .el-date-range-picker__header {
          width: 230px;
        }
      }
    }
  }

  &.has-calendar {
    .dropdown__popper {
      width: 734px;
      max-width: none;
      height: 278px;
    }
  }
}
</style>
